import {
  forwardRef,
  type ComponentProps,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { Eye, EyeOff, Moon, Sun } from 'lucide-react';
import { Input } from '@vnetwork/ui/primitives';
import { useTheme } from '@vnetwork/ui/provider';
import { brand as brandConfig } from '@/config/brand';
import { WafFreeAuthPanel } from './WafFreeAuthPanel';
import styles from './AuthShell.module.css';

// Copied 1:1 from @vnetwork/ui src/templates/_shells/AuthShell.tsx — templates
// are reference compositions the package does not publish, so they are mirrored
// into the app. Only the Input import path and the default logoSrc differ.

export interface AuthShellProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Small label above the title (e.g. "— business"). */
  eyebrow?: ReactNode;
  /** Banner rendered above the card (e.g. an info notice). */
  notice?: ReactNode;
  /** The form / body. */
  children: ReactNode;
  /** Bottom helper line (e.g. "Don't have an account? Sign up"). */
  footer?: ReactNode;
  /** Optional brand panel — when set, renders a split-screen layout. */
  brand?: ReactNode;
  /** Optional promo panel shown beside the card in the centered layout (e.g. a
   *  campaign banner on the right of the sign-up form). Takes precedence over `brand`. */
  aside?: ReactNode;
  /** Optional pane forming the other half of a single split block (form | pane).
   *  Takes precedence over `aside` and `brand`. */
  splitPane?: ReactNode;
  /** Redesigned split: the form on the LEFT with a top bar (theme toggle +
   *  `headerEnd`) and a full-height branded panel on the RIGHT (the slot the VScanner
   *  banner will fill). Takes precedence over every
   *  other layout mode. Used by the sign in / sign up flow. */
  brandPanel?: ReactNode;
  /** Content next to the theme toggle in the redesign top bar (e.g. a "New here? Sign up"
   *  cross-link). Only rendered in `brandPanel` mode. */
  headerEnd?: ReactNode;
  /** Opt out of the default redesign split (branded panel) and render the plain
   *  centered card instead. */
  plain?: boolean;
  /** Redesign heading scale: 26/32 title + 14/21 subtitle (sign in, business /
   *  individual forms) instead of the 30/36 + 17/25 default. */
  compact?: boolean;
  /** Logo image URL shown above the title. */
  logoSrc?: string;
}

/** Light/dark toggle for the redesign form pane top bar. */
function ThemeToggleButton() {
  const [theme, setTheme] = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      className={styles.themeToggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
    >
      {isDark ? <Sun size={15} /> : <Moon size={15} />}
    </button>
  );
}

/**
 * Centered authentication layout (sign in / sign up). Pass `brand` to switch
 * to a split-screen layout with a brand panel on the left.
 */
export function AuthShell({
  title,
  subtitle,
  eyebrow,
  notice,
  children,
  footer,
  brand,
  aside,
  splitPane,
  brandPanel,
  headerEnd,
  plain,
  compact,
  logoSrc = brandConfig.assets.icon,
}: AuthShellProps) {
  // Default every auth screen to the redesign split (form left, branded panel right).
  // Pages can pass an explicit `brandPanel`, opt out with `plain`, or use a legacy
  // layout mode (splitPane / aside / brand).
  const autoPanel =
    brandPanel ?? (!plain && brandConfig.promos.wafFree ? <WafFreeAuthPanel /> : undefined);

  const card = (
    <div className={styles.cardWrap}>
      <img className={styles.logo} src={logoSrc} alt={brandConfig.shortName} width={44} height={44} />
      {notice ? <div className={styles.notice}>{notice}</div> : null}
      <div className={styles.card}>
        {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
        <h1 className={styles.title}>{title}</h1>
        {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
        <div className={styles.body}>{children}</div>
      </div>
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </div>
  );

  // Redesigned auth split: form LEFT (440px column) with a top bar (theme toggle +
  // cross-link), branded panel RIGHT (764px column, inset rounded card). Below 960px
  // the panel stacks under the form. The panel slot is where the VScanner banner
  // will drop in.
  if (autoPanel && !splitPane && !aside && !brand) {
    return (
      <div className={styles.redesign}>
        <header className={styles.redesignTopbar}>
          <ThemeToggleButton />
          {headerEnd ? <div className={styles.redesignHeaderEnd}>{headerEnd}</div> : null}
        </header>
        <div className={styles.redesignMain}>
          <div className={`${styles.redesignForm} ${compact ? styles.compact : ''}`}>
            {eyebrow ? <div className={styles.redesignEyebrow}>{eyebrow}</div> : null}
            <h1 className={styles.redesignTitle}>{title}</h1>
            {subtitle ? <p className={styles.redesignSubtitle}>{subtitle}</p> : null}
            {notice ? <div className={styles.notice}>{notice}</div> : null}
            <div className={styles.redesignContent}>{children}</div>
            {footer ? <div className={styles.redesignFooter}>{footer}</div> : null}
          </div>
        </div>
        <aside className={styles.redesignPanel}>{autoPanel}</aside>
      </div>
    );
  }

  // Single split block: form on one half, promo pane on the other. Logo + footer sit
  // INSIDE the form half. Stacks on mobile.
  if (splitPane) {
    return (
      <div className={styles.centered}>
        <div className={styles.splitCard}>
          <div className={styles.splitForm}>
            <img
              className={styles.splitLogo}
              src={logoSrc}
              alt={brandConfig.shortName}
              width={40}
              height={40}
            />
            {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
            <h1 className={styles.title}>{title}</h1>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
            {notice ? <div className={styles.notice}>{notice}</div> : null}
            <div className={styles.body}>{children}</div>
            {footer ? <div className={styles.splitFooter}>{footer}</div> : null}
          </div>
          <div className={styles.splitAside}>{splitPane}</div>
        </div>
      </div>
    );
  }

  // Centered card with a promo panel to its right (hidden on narrow viewports).
  if (aside) {
    return (
      <div className={styles.centered}>
        <div className={styles.pair}>
          {card}
          <aside className={styles.aside}>{aside}</aside>
        </div>
      </div>
    );
  }

  if (brand) {
    return (
      <div className={styles.split}>
        <div className={styles.brand}>{brand}</div>
        <div className={styles.formPane}>{card}</div>
      </div>
    );
  }

  return <div className={styles.centered}>{card}</div>;
}

/**
 * Password field with a show/hide toggle — reused by sign in / sign up.
 * (forwardRef is the only change from the template's version, so react-hook-form
 * can register the underlying input.)
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> & {
    invalid?: boolean;
    wrapperClassName?: string;
    inputSize?: ComponentProps<typeof Input>['inputSize'];
  }
>(function PasswordInput(props, ref) {
  const [show, setShow] = useState(false);
  return (
    <Input
      ref={ref}
      type={show ? 'text' : 'password'}
      endSlot={
        <button
          type="button"
          className={styles.reveal}
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow((s) => !s)}
        >
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      }
      {...props}
    />
  );
});
